import React, { FC, useState, ReactElement } from 'react'
import * as yup from 'yup'
import { Formik } from 'formik'
import { Form, Alert, Row, Col } from 'react-bootstrap'
import Auth from '@aws-amplify/auth'
import { RouteComponentProps, withRouter, Link } from 'react-router-dom'

import LoadingButton from '../../LoadingButton'

interface MatchParams {
  email: string
}

const schema = yup.object({
  confirmationCode: yup.string().required('Confirmation code is required'),
  newPassword: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirmPassword: yup.string().oneOf([yup.ref('newPassword'), null], 'Passwords must match'),
})

const ResetPasswordForm: FC<RouteComponentProps<MatchParams>> = ({ match }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [resetSuccessful, setResetSuccessful] = useState(false)
  const { email } = match.params

  const resetPassword = async (values: {
    confirmationCode: string
    newPassword: string
    confirmPassword: string
  }): Promise<void> => {
    setIsLoading(true)

    try {
      await Auth.forgotPasswordSubmit(email, values.confirmationCode, values.newPassword)

      setErrorMessage('')
      setIsLoading(false)
      setResetSuccessful(true)
    } catch (e) {
      setErrorMessage(e.message)
      setIsLoading(false)
      setResetSuccessful(false)
    }
  }

  if (resetSuccessful) {
    return (
      <div className="success">
        <Row>
          <Col>
            <h4>Your password has been reset.</h4>
          </Col>
        </Row>
        <Row>
          <Col>
            <Link to="/login">
              <h6>Click here to login with your new credentials.</h6>
            </Link>
          </Col>
        </Row>
      </div>
    )
  }

  return (
    <>
      {errorMessage && (
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <hr />
          <span>{errorMessage}</span>
        </Alert>
      )}
      <Formik
        validationSchema={schema}
        onSubmit={resetPassword}
        initialValues={{
          confirmationCode: '',
          newPassword: '',
          confirmPassword: '',
        }}
      >
        {({ handleSubmit, values, handleChange, touched, errors, submitCount }): ReactElement => (
          <Form noValidate onSubmit={handleSubmit}>
            <Form.Group controlId="confirmationCode">
              <Form.Label>Confirmation Code</Form.Label>
              <Form.Control
                type="text"
                required
                value={values.confirmationCode}
                onChange={handleChange}
                isValid={touched.confirmationCode && !errors.confirmationCode}
                isInvalid={submitCount > 0 && !!errors.confirmationCode}
              />
              <Form.Control.Feedback type="invalid">
                <Alert variant="danger">{errors.confirmationCode}</Alert>
              </Form.Control.Feedback>
              <h6>{`Please check your email (${email}) for your confirmation code.`}</h6>
            </Form.Group>
            <div className="password-requirements">
              <Row className="title">
                <Col>
                  <h5>Password Requirements</h5>
                </Col>
              </Row>
              <Row className="requirements">
                <Col>
                  <ul>
                    <li>Minimum of 8 characters</li>
                    <li>At least 1 number</li>
                    <li>At least 1 special character</li>
                    <li>Uppercase letters</li>
                    <li>Lowercase letters</li>
                  </ul>
                </Col>
              </Row>
            </div>
            <Form.Group controlId="newPassword">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                required
                value={values.newPassword}
                onChange={handleChange}
                isValid={touched.newPassword && !errors.newPassword}
                isInvalid={submitCount > 0 && !!errors.newPassword}
              />
              <Form.Control.Feedback type="invalid">
                <Alert variant="danger">{errors.newPassword}</Alert>
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="confirmPassword">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                required
                value={values.confirmPassword}
                onChange={handleChange}
                isValid={touched.confirmPassword && !errors.confirmPassword}
                isInvalid={submitCount > 0 && !!errors.confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                <Alert variant="danger">{errors.confirmPassword}</Alert>
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="actions" className="actions">
              <LoadingButton
                className="reset-password-button"
                variant="primary"
                type="submit"
                block
                isLoading={isLoading}
              >
                RESET PASSWORD
              </LoadingButton>
            </Form.Group>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default withRouter(ResetPasswordForm)
